import { useState, useEffect, Fragment } from 'react'
import { getUserData, isAuthenticated } from '../utils/auth';
import { apiRequest, getUserCredits, trickleCreateObject, trickleListObjects, trickleUpdateObject } from '../utils/api';
import SubscriptionForm from '../components/payments/SubscriptionForm';
import CheckoutForm from '../components/payments/CheckoutForm';

import {loadStripe} from '@stripe/stripe-js';

function Payment() {
        
    // Define subscription plans
    const subscriptionPlans = [
        { id: 'price_basic', name: '基本方案', amount: 9.99, credits: 100, period: '月' },
        { id: 'price_pro', name: '專業方案', amount: 19.99, credits: 250, period: '月' },
        { id: 'price_premium', name: '高級方案', amount: 39.99, credits: 600, period: '月' }
    ];

    const [selectedTab, setSelectedTab] = useState('oneTime');
    const [amount, setAmount] = useState('10.00');
    const [isSuccess, setIsSuccess] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(subscriptionPlans[0]);
        
    useEffect(() => {
        // Check authentication
        if (!isAuthenticated()) {
            window.location.href = '/auth';
            return;
        }
    }, []);

    try {
        const handlePaymentSuccess = async (result) => {
            try {
                setIsProcessing(true);
                setError('');
                
                // Update user credits
                const creditAmount = Math.floor(parseFloat(amount) * 10); // 10 credits per dollar
                // await updateUserCredits(creditAmount);
                
                setIsSuccess(true);
                setPaymentResult({
                    amount: parseFloat(amount),
                    credits: creditAmount,
                    timestamp: new Date().toISOString()
                });
                
                // Record the payment transaction
                // await recordPaymentTransaction(parseFloat(amount), creditAmount, result);
            } catch (error) {
                console.error('Failed to process payment success:', error);
                setError('處理付款成功後發生錯誤，但您的付款已經完成。請聯繫客服以確認點數是否已添加。');
            } finally {
                setIsProcessing(false);
            }
        };
        
        const handlePaymentError = (error) => {
            console.error('Payment error:', error);
            setError(error.message || '付款處理失敗，請稍後再試');
            setIsProcessing(false);
        };
        
        const updateUserCredits = async (creditAmount) => {
            try {
                const userData = getUserData();
                if (!userData) return;
                
                const creditData = await getUserCredits(userData.id);
                const newCredits = creditData.credits + creditAmount;
                
                const transaction = {
                    operation: 'purchase_credits',
                    timestamp: new Date().toISOString(),
                    details: { amount: parseFloat(amount), credits: creditAmount }
                };
                
                const history = [...(creditData.history || []), transaction];
                
                await trickleUpdateObject('user_credits', creditData.id, {
                    userId: userData.id,
                    credits: newCredits,
                    history
                });
                
                // Dispatch credit update event
                const event = new CustomEvent('creditUpdate', {
                    detail: { credits: newCredits }
                });
                window.dispatchEvent(event);
                
                return newCredits;
            } catch (error) {
                console.error('Failed to update credits:', error);
                throw error;
            }
        };
        
        const recordPaymentTransaction = async (amount, credits, paymentResult) => {
            try {
                const userData = getUserData();
                if (!userData) return;
                
                const transactionData = {
                    userId: userData.id,
                    userEmail: userData.email,
                    userName: userData.name,
                    amount: amount,
                    currency: 'HKD',
                    credits: credits,
                    paymentMethod: 'credit_card',
                    paymentId: paymentResult?.id || `manual_${Date.now()}`,
                    status: 'succeeded',
                    createdAt: new Date().toISOString()
                };
                
                await trickleCreateObject('payment_transactions', transactionData);
            } catch (error) {
                console.error('Failed to record payment transaction:', error);
                // Don't throw error here as this is not critical for the user experience
            }
        };
        
        return (
            <div className="container mx-auto px-4 py-8" data-name="payment-page">
                <div className="max-w-3xl mx-auto">
                    <h1 className="text-3xl font-bold mb-8 text-center">購買點數</h1>
                    
                    {!isSuccess ? (
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <div className="p-6">
                                <div className="flex border-b border-gray-200 mb-6">
                                    <button
                                        onClick={() => setSelectedTab('oneTime')}
                                        className={`py-2 px-4 font-medium ${selectedTab === 'oneTime' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        data-name="one-time-tab"
                                    >
                                        單次購買
                                    </button>
                                    <button
                                        onClick={() => setSelectedTab('subscription')}
                                        className={`py-2 px-4 font-medium ${selectedTab === 'subscription' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        data-name="subscription-tab"
                                    >
                                        訂閱方案
                                    </button>
                                </div>
                                
                                {error && (
                                    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded" data-name="error-message">
                                        <div className="flex">
                                            <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                            <p className="text-red-700">{error}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedTab === 'oneTime' ? (
                                    <div data-name="one-time-payment">
                                        <div className="mb-6">
                                            <h2 className="text-xl font-semibold mb-4">購買點數</h2>
                                            <p className="text-gray-600 mb-4">
                                                透過單次付款為您的帳戶添加點數。每 $1 可獲得 10 點點數。
                                            </p>
                                            
                                            <div className="mb-4">
                                                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                                                    金額 (HKD)
                                                </label>
                                                <div className="mt-1 relative rounded-md shadow-sm">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <span className="text-gray-500 sm:text-sm">$</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        name="amount"
                                                        id="amount"
                                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                                                        placeholder="0.00"
                                                        aria-describedby="price-currency"
                                                        value={amount}
                                                        onChange={(e) => setAmount(e.target.value)}
                                                        min="5.00"
                                                        step="1.00"
                                                        data-name="amount-input"
                                                        disabled={isProcessing}
                                                    />
                                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                        <span className="text-gray-500 sm:text-sm" id="price-currency">
                                                            HKD
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-blue-50 p-4 rounded-md mb-6">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium">您將獲得的點數：</span>
                                                    <span className="text-xl font-bold">{Math.floor(parseFloat(amount || 0) * 10)}</span>
                                                </div>
                                            </div>
                                            
                                            <button 
                                                onClick={() => setShowPaymentForm(true)}
                                                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all flex items-center justify-center"
                                                disabled={isProcessing || !amount || parseFloat(amount) < 5}
                                                data-name="create-payment-button"
                                            >
                                                {isProcessing ? (
                                                    <Fragment>
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                                        處理中...
                                                    </Fragment>
                                                ) : (
                                                    <Fragment>
                                                        <i className="fas fa-credit-card mr-2"></i>
                                                        前往付款
                                                    </Fragment>
                                                )}
                                            </button>
                                        </div>
                                        
                                        {showPaymentForm && (
                                            <CheckoutForm 
                                                amount={parseFloat(amount || 0)} 
                                                currency="HKD"
                                                onSuccess={handlePaymentSuccess}
                                                onError={handlePaymentError}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div data-name="subscription-payment">
                                        <div className="mb-6">
                                            <h2 className="text-xl font-semibold mb-4">訂閱方案</h2>
                                            <p className="text-gray-600 mb-6">
                                                選擇訂閱方案，每月自動獲得點數。
                                            </p>
                                            
                                            <div className="grid md:grid-cols-3 gap-4 mb-6">
                                                {subscriptionPlans.map((plan) => (
                                                    <div 
                                                        key={plan.id}
                                                        onClick={() => setSelectedPlan(plan)}
                                                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                                            selectedPlan.id === plan.id 
                                                                ? 'border-blue-500 bg-blue-50' 
                                                                : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                        data-name={`plan-${plan.id}`}
                                                    >
                                                        <div className="flex items-center mb-2">
                                                            <input
                                                                type="radio"
                                                                name="plan"
                                                                checked={selectedPlan.id === plan.id}
                                                                onChange={() => setSelectedPlan(plan)}
                                                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                            />
                                                            <label className="ml-3 font-medium">
                                                                {plan.name}
                                                            </label>
                                                        </div>
                                                        <div className="ml-7">
                                                            <p className="text-2xl font-bold">${plan.amount}<span className="text-sm font-normal text-gray-500">/{plan.period}</span></p>
                                                            <p className="text-sm text-gray-600">{plan.credits} 點數/{plan.period}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <SubscriptionForm 
                                            priceId={selectedPlan.id}
                                            planName={selectedPlan.name}
                                            amount={selectedPlan.amount}
                                            billingPeriod={selectedPlan.period}
                                            onSuccess={handlePaymentSuccess}
                                            onError={handlePaymentError}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden p-8 text-center" data-name="success-message">
                            <div className="mb-6 text-green-500">
                                <i className="fas fa-check-circle text-6xl"></i>
                            </div>
                            <h2 className="text-2xl font-bold mb-2">謝謝您！</h2>
                            <p className="text-xl mb-6">您的付款已成功。</p>
                            <p className="mb-6 text-gray-600">
                                {selectedTab === 'oneTime' ? (
                                    <span>您已添加 <strong>{Math.floor(parseFloat(amount) * 10)} 點數</strong> 到您的帳戶。</span>
                                ) : (
                                    <span>您已成功訂閱 <strong>{selectedPlan.name}</strong>。</span>
                                )}
                            </p>
                            <div className="flex justify-center space-x-4">
                                <button
                                    onClick={() => window.location.href = '/workflow'}
                                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    data-name="go-to-tools"
                                >
                                    開始使用工具
                                </button>
                                <button
                                    onClick={() => {
                                        setIsSuccess(false);
                                        setPaymentResult(null);
                                        setShowPaymentForm(false);
                                    }}
                                    className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50"
                                    data-name="make-another-payment"
                                >
                                    再次購買
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-8 bg-gray-50 p-6 rounded-lg border border-gray-200" data-name="payment-info">
                        <h3 className="text-lg font-medium mb-4">付款資訊</h3>
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <i className="fas fa-lock text-gray-500 mr-3"></i>
                                <p className="text-gray-600">您的付款資訊受到加密和安全保護。</p>
                            </div>
                            <div className="flex items-center">
                                <i className="fas fa-credit-card text-gray-500 mr-3"></i>
                                <p className="text-gray-600">我們接受所有主要的信用卡和借記卡。</p>
                            </div>
                            <div className="flex items-center">
                                <i className="fas fa-question-circle text-gray-500 mr-3"></i>
                                <p className="text-gray-600">
                                    需要幫助？<a href="/contact" className="text-blue-600 hover:underline">聯繫我們的支援團隊</a>。
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('Payment page error:', error);
        reportError(error);
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <div className="bg-red-100 p-6 rounded-lg">
                    <h2 className="text-xl font-bold text-red-700 mb-2">付款錯誤</h2>
                    <p>處理您的付款時發生錯誤。請稍後再試。</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        重新載入頁面
                    </button>
                </div>
            </div>
        );
    }
}

export default Payment;